import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./dialog";
import { Button } from "./button";
import { Input } from "./input";
import { Badge } from "./badge";
import { GripVertical } from "lucide-react";

export interface ColumnDefinition {
  key: string;
  label: string;
  type: "text" | "number" | "date" | "chip" | "action";
  sortable?: boolean;
  filterable?: boolean;
  width?: string;
  required?: boolean;
}

interface ColumnPickerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  columns: ColumnDefinition[];
  visibleColumns: string[];
  columnOrder?: string[];
  onColumnVisibilityChange: (visibleColumns: string[], columnOrder: string[]) => void;
}

const ColumnPicker: React.FC<ColumnPickerProps> = ({
  open,
  onClose,
  title,
  columns,
  visibleColumns,
  columnOrder,
  onColumnVisibilityChange,
}) => {
  const defaultOrder = useMemo(() => columns.map((col) => col.key), [columns]);

  const [localVisibleColumns, setLocalVisibleColumns] = useState<string[]>(visibleColumns);
  const [localColumnOrder, setLocalColumnOrder] = useState<string[]>(
    columnOrder?.length ? columnOrder : defaultOrder
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [dragKey, setDragKey] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setLocalVisibleColumns(visibleColumns);
    const base = columnOrder?.length ? columnOrder : defaultOrder;
    const known = new Set(columns.map((c) => c.key));
    const merged = [...base.filter((k) => known.has(k)), ...columns.map((c) => c.key).filter((k) => !base.includes(k))];
    setLocalColumnOrder(merged);
    setSearchTerm("");
  }, [open, visibleColumns, columnOrder, columns, defaultOrder]);

  const columnByKey = useMemo(() => {
    const m = new Map<string, ColumnDefinition>();
    columns.forEach((c) => m.set(c.key, c));
    return m;
  }, [columns]);

  const orderedRows = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return localColumnOrder
      .map((key) => columnByKey.get(key))
      .filter((col): col is ColumnDefinition => Boolean(col))
      .filter((col) => !q || col.label.toLowerCase().includes(q));
  }, [localColumnOrder, columnByKey, searchTerm]);

  const moveKeyBefore = useCallback((draggedKey: string, targetKey: string) => {
    if (draggedKey === targetKey) return;
    setLocalColumnOrder((prev) => {
      const next = prev.filter((k) => k !== draggedKey);
      const insertAt = Math.max(0, next.indexOf(targetKey));
      next.splice(insertAt, 0, draggedKey);
      return next;
    });
  }, []);

  const handleSave = () => {
    onColumnVisibilityChange(localVisibleColumns, localColumnOrder);
    onClose();
  };

  const handleCancel = () => {
    setLocalVisibleColumns(visibleColumns);
    setLocalColumnOrder(columnOrder?.length ? columnOrder : defaultOrder);
    onClose();
  };

  const handleShowAll = () => {
    setLocalVisibleColumns(columns.map((col) => col.key));
  };

  const handleHideAll = () => {
    const requiredKeys = columns.filter((col) => col.required).map((col) => col.key);
    setLocalVisibleColumns(requiredKeys);
  };

  const visibleColumnsCount = localVisibleColumns.length;
  const totalColumnsCount = columns.length;
  const availableColumns = orderedRows.filter((column) => !localVisibleColumns.includes(column.key));
  const selectedColumns = localColumnOrder
    .map((key) => columnByKey.get(key))
    .filter((column): column is ColumnDefinition => Boolean(column))
    .filter((column) => localVisibleColumns.includes(column.key));

  const addColumnToSelection = (columnKey: string) => {
    if (!localVisibleColumns.includes(columnKey)) {
      setLocalVisibleColumns((prev) => [...prev, columnKey]);
    }
    if (!localColumnOrder.includes(columnKey)) {
      setLocalColumnOrder((prev) => [...prev, columnKey]);
    }
  };

  const removeColumnFromSelection = (columnKey: string) => {
    const column = columnByKey.get(columnKey);
    if (column?.required) return;
    setLocalVisibleColumns((prev) => prev.filter((k) => k !== columnKey));
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) handleCancel();
      }}
    >
      <DialogContent className="flex max-h-[min(92vh,920px)] w-[min(98vw,76rem)] flex-col gap-0 p-0 sm:max-w-none">
        <DialogHeader className="shrink-0 space-y-2 border-b border-border px-6 py-4 text-left">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <GripVertical className="h-5 w-5 text-primary" aria-hidden />
            {title}
          </DialogTitle>
          <DialogDescription>Drag columns from available to selected, then reorder selected columns by dragging.</DialogDescription>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-4 px-6 py-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <Input
              placeholder="Search columns…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="glass-card sm:max-w-xs"
            />
            <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
              <Button type="button" variant="outline" size="sm" onClick={handleShowAll}>
                Show all
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={handleHideAll}>
                Hide all
              </Button>
              <Badge variant="secondary" className="tabular-nums">
                {visibleColumnsCount} / {totalColumnsCount} visible
              </Badge>
            </div>
          </div>

          <div className="grid min-h-0 flex-1 gap-4 xl:grid-cols-[1.2fr_1fr]">
            <div className="min-h-0 overflow-y-auto rounded-lg border border-border bg-muted/20 p-3 sm:p-4">
              <div className="mb-3">
                <p className="text-sm font-medium">Available columns</p>
                <p className="text-xs text-muted-foreground">Drag from here into selected columns.</p>
              </div>
              <div
                className="grid grid-cols-1 gap-2 md:grid-cols-2"
                onDragOver={(e) => {
                  if (!dragKey) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  const from = e.dataTransfer.getData("text/plain") || dragKey;
                  if (from) {
                    removeColumnFromSelection(from);
                  }
                  setDragKey(null);
                }}
              >
                {availableColumns.map((column) => {
                  const isDragging = dragKey === column.key;
                  return (
                    <div
                      key={column.key}
                      draggable
                      onDragStart={(e) => {
                        setDragKey(column.key);
                        e.dataTransfer.effectAllowed = "move";
                        e.dataTransfer.setData("text/plain", column.key);
                      }}
                      onDragEnd={() => setDragKey(null)}
                      onDoubleClick={() => addColumnToSelection(column.key)}
                      className={`flex items-center gap-3 rounded-md border border-border bg-background px-3 py-2.5 shadow-sm transition-colors ${
                        isDragging ? "opacity-60 ring-2 ring-primary" : ""
                      }`}
                    >
                      <GripVertical className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <div className="flex flex-1 flex-col gap-0.5">
                        <span className="font-medium leading-none">{column.label}</span>
                        <span className="text-xs text-muted-foreground">
                          {column.sortable ? "Sortable" : "Display only"}
                          {column.filterable ? " · Quick filters" : ""}
                        </span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => addColumnToSelection(column.key)}
                      >
                        Add
                      </Button>
                    </div>
                  );
                })}
              </div>
              {availableColumns.length === 0 && (
                <div className="py-12 text-center text-sm text-muted-foreground">No columns match your search.</div>
              )}
            </div>

            <div className="min-h-0 overflow-y-auto rounded-lg border border-border bg-background p-3 sm:p-4">
              <div className="mb-3">
                <p className="text-sm font-medium">Selected columns</p>
                <p className="text-xs text-muted-foreground">Drag to reorder. This order maps directly to your table.</p>
              </div>
              <div
                className="space-y-2"
                onDragOver={(e) => {
                  if (!dragKey) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  const from = e.dataTransfer.getData("text/plain") || dragKey;
                  if (from) {
                    addColumnToSelection(from);
                  }
                  setDragKey(null);
                }}
              >
                {selectedColumns.map((column, index) => {
                  const isDragging = dragKey === column.key;
                  return (
                    <div
                      key={`preview-${column.key}`}
                      draggable={!column.required}
                      onDragStart={(e) => {
                        if (column.required) return;
                        setDragKey(column.key);
                        e.dataTransfer.effectAllowed = "move";
                        e.dataTransfer.setData("text/plain", column.key);
                      }}
                      onDragOver={(e) => {
                        if (!dragKey || dragKey === column.key) return;
                        e.preventDefault();
                        e.dataTransfer.dropEffect = "move";
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        const from = e.dataTransfer.getData("text/plain") || dragKey;
                        if (from) {
                          addColumnToSelection(from);
                          moveKeyBefore(from, column.key);
                        }
                        setDragKey(null);
                      }}
                      onDragEnd={() => setDragKey(null)}
                      className={`flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm ${
                        isDragging ? "opacity-60 ring-2 ring-primary" : ""
                      }`}
                    >
                      <GripVertical className={`h-4 w-4 ${column.required ? "opacity-40" : "text-muted-foreground"}`} />
                      <span className="inline-flex h-5 min-w-5 items-center justify-center rounded bg-muted text-xs font-medium">
                        {index + 1}
                      </span>
                      <span className="flex-1">{column.label}</span>
                      {column.required ? (
                        <Badge variant="outline" className="text-[10px] font-normal">Required</Badge>
                      ) : (
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeColumnFromSelection(column.key)}>
                          Remove
                        </Button>
                      )}
                    </div>
                  );
                })}
                {selectedColumns.length === 0 && (
                  <div className="rounded-md border border-dashed border-border px-3 py-6 text-center text-sm text-muted-foreground">
                    Drop columns here to display them in the table.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="shrink-0 border-t border-border px-6 py-4">
          <Button type="button" variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button type="button" variant="gradient" onClick={handleSave}>
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export { ColumnPicker };
